import {
  ExternalBrand,
  type FifteenDigitDTOEntry,
  type HousingDTO,
  type ProductDataDTO,
  type TechnicalDetailsDataDTO,
  type TechnicalDetailsDataEntryDTO,
} from "../../dto/ProductDataDTO";
import {
  type ConfiguratorProductData,
  type FifteenDigitData,
  type HousingProduct,
  HousingProductType,
  HousingType,
  type KITProductData,
  type PlainProductData,
  type ProductData,
  ProductType,
} from "../../models/ProductData";
import * as WeightUtils from "../../utils/WeightUtils";
import { WeightUnit } from "../../utils/WeightUtils";
import { type ProductLinearInfoDTO } from "../../Cart/dto/CartViewDTO";
import { createLinearProductCode } from "../../utils/cartHelper";
import { transformFeatures } from "../../Search/transformers/SearchResultDataTransformer";
import { getToolTypeFromString } from "../../feature/LaunchRelevantTool/ToolType";
import { processSalesStatus } from "~/ProductDetails/components/PhaseInOut/SalesStatus";
import { type TechnicalDetailsData, type TechnicalDetailsDataEntry } from "~/ProductDetails/dto/PdpPageProductDataDto";

import { isNonNull } from "~/utils/reducers";
import { getConfiguratorTypeFromProductCode } from "~/Configurators/ConfiguratorType";
import { type TranslationModel, TranslationType } from "~/common/TranslationModel";

export function transformLinear(dto: ProductDataDTO, productLinearInfo: ProductLinearInfoDTO): ConfiguratorProductData {
  const configuratorType = getConfiguratorTypeFromProductCode(dto.code);
  if (!configuratorType) {
    throw new Error("Unknown configurator type");
  }
  return {
    ...transformProduct(dto),
    type: ProductType.CONFIGURATOR,
    configuratorType,
    subtype: productLinearInfo.guideways && productLinearInfo.carriages ? "unit" : "part",
    code: createLinearProductCode(dto.code),
    images: productLinearInfo.images || [],
    designation: productLinearInfo.designation,
    sapMaterialNumber: productLinearInfo.sapMaterialNumber,
    aliases: [productLinearInfo.aliasesString],
    leadTime: productLinearInfo.leadTime,
    guideway: productLinearInfo.guideways ?? "",
    carriages: productLinearInfo.carriages ?? "",
    configId: productLinearInfo.configId ?? "",
  };
}

export function transformProductShortDescription(
  dto: Pick<ProductDataDTO, "shortDescription" | "includedParts" | "optionalParts">,
): TranslationModel {
  if (dto.includedParts?.length) {
    return {
      type: TranslationType.INTERNAL,
      value: "housing.shortDescription.UNIVERSAL",
    };
  } else if (dto.optionalParts?.length) {
    return {
      type: TranslationType.INTERNAL,
      value: "housing.shortDescription.KIT",
    };
  } else {
    return {
      type: TranslationType.RAW_TEXT,
      value: dto.shortDescription,
    };
  }
}

export function transformProduct(dto: ProductDataDTO, includeFeatures = true): ProductData {
  const product: PlainProductData = transformBase(dto, includeFeatures);

  if (dto.includedParts?.length || dto.optionalParts?.length) {
    const kitProduct: KITProductData = {
      ...product,
      type: ProductType.KIT,
      shortDescription: transformProductShortDescription(dto),
      /* HOUSING/KIT PROPERTIES */
      housingType: HousingType[dto.housingType?.code ?? ""] ?? undefined,
      // ignore wrong data with no product attached
      optionalParts: (dto.optionalParts ?? []).filter((x) => x.product).map(transformHousingProducts),
      includedParts: (dto.includedParts ?? []).filter((x) => x.product).map(transformHousingProducts),
      pnaCode: dto.housingVariantCode!,
    };
    return kitProduct;
  }

  return product;
}

function transformBase(dto: ProductDataDTO, includeFeatures: boolean): PlainProductData {
  let weight = undefined;
  let weightImperial = undefined;
  if (dto.mass && dto.massUnit) {
    weight = WeightUtils.convert(WeightUtils.getWeightObject(dto.mass, dto.massUnit), WeightUnit.kg);
  }
  if (dto.massImperial && dto.massImperialUnit) {
    weightImperial = WeightUtils.convert(
      WeightUtils.getWeightObject(dto.massImperial, dto.massImperialUnit),
      WeightUnit.lbs,
    );
  }
  const productAllCategories = dto.productAllCategories?.slice() ?? [];
  productAllCategories.sort();
  dto.productAllCategories = productAllCategories;

  const oldDesignation =
    dto.oldDesignation && dto.oldDesignation.length > 0 ? dto.oldDesignation.join(", ") : undefined;

  const shortDescription: TranslationModel = {
    type: TranslationType.RAW_TEXT,
    value: dto.shortDescription,
  };

  const configuratorTypes = (dto.relevantConfigurators ?? [])
    .map((item) => getToolTypeFromString(item.code))
    .filter(isNonNull);

  const salesCampaignList = dto.salesCampaigns ?? {};

  return {
    code: dto.code,
    hybrisCode: dto.code,
    pnaCode: dto.code,
    type: ProductType.DEFAULT,
    manufacturerLogoUrl: dto.manufacturerLogoUrl,
    brandLogoUrl: dto.brandLogoUrl,
    images: dto.images || [],
    designation: dto.designation ?? "",
    marketDesignation: dto.marketDesignation,
    displayMMDAssistant: dto.isMMDAssistantAvailableForProduct,
    cmnSearchMatched: dto.isCMNSearchMatch,
    sapMaterialNumber: dto.sapMaterialNumber ?? "",
    shortDescription,
    description: dto.description,
    preferred: dto.preferred ?? dto.price?.preferred ?? false,
    url: dto.url,
    packageAliases: dto.packageAliases ?? {},
    aliases: transformAliases(dto.aliases),
    baseType: dto.baseTypeNorm || "",
    productLineId: dto.productLineId || "",
    productAllCategories,
    oldDesignation,
    isOptime: dto.isOptime ?? false,
    quantityVisible: dto.quantityVisible ?? true,
    isPMA: dto.isPMA,
    marketType: dto.type ?? null,
    weight,
    weightImperial,
    fifteenDigit: transformFifteenDigitData(dto.fifteenDigit),
    gpsdGroup: dto.gpsdGroup,
    successorProduct: dto.succeedingProduct ? transformProduct(dto.succeedingProduct, includeFeatures) : undefined,
    alternativeProducts: (dto.alternativeProducts ?? [])
      // Filter out invalid products - they shouldn't have been sent from BE in the first place...
      .filter((dto) => dto?.code)
      .map((x) => transformProduct(x, includeFeatures)),

    features: includeFeatures ? transformFeatures(dto) : [],
    primaryImage: dto.images ? dto.images.filter((img) => img.format === "thumbnail")[0] : undefined,

    technicalDetails: (dto.technicalDetails || []).map(transformTechnicalDetails),

    customerRequestedCADAvailability: dto.customerRequestedCADAvailability,

    salesCampaign: salesCampaignList ?? {},

    /* HOUSING RELATED PROPERTIES */
    numberOfBolts: dto.numberOfBolts ?? "",

    configuratorTypes,

    showPostCartModal: dto.showPostCartModal,
    externalBrand: dto.externalBrand,
    externalProduct: dto.externalBrand === ExternalBrand.Ewellix,
    reconditionable: dto.reconditionable,
    additionalCalculationUrl: dto.additionalCalculationUrl,
    isProductBackInStock: dto.isProductBackInStock,
  };
}

export const transformTechnicalDetails = (dto: TechnicalDetailsDataDTO): TechnicalDetailsData => {
  return {
    ...dto,
    entries: dto.entries.map(transformTechnicalDetailsEntry),
  };
};

export const transformTechnicalDetailsEntry = (dto: TechnicalDetailsDataEntryDTO): TechnicalDetailsDataEntry => {
  return {
    ...dto,
    values: Array.isArray(dto.value) ? dto.value : [dto.value],
  };
};

export const transformFifteenDigitData = (data?: FifteenDigitDTOEntry[]): FifteenDigitData => {
  if (!data) {
    return {};
  }
  const base: FifteenDigitData = {};

  data.forEach((digit, i) => {
    if (!digit.packSize) {
      console.warn(`Pack size not defined for ${digit.materialNo} - setting to 1.`);
      digit.packSize = 1;
    }
    let customsHolder: string[] = [];
    if (Array.isArray(digit.customsData) && digit.customsData.length > 0) {
      customsHolder = digit.customsData.map((c) => c.customsTariffNumber);
    }
    // country of origin exists but no material no
    if (digit.materialNo === undefined || digit.materialNo === null) {
      const current = i + 1;
      // set a fake key so we can display country of origin
      digit.materialNo = "NoMaterial-" + (current < 10 ? "0" + current : current + "");
    }
    base[digit.materialNo.slice(-2) + ""] = {
      ...digit,
      packSize: digit.packSize,
      customsData: customsHolder,
      countryOfOrigin: digit.countryOfOrigin ?? [],
      schaefflerEan: digit.schaefflerEan ?? "",
      salesStatus: processSalesStatus(digit.salesStatusData?.[0]?.salesStatus),
    };
  });

  return base;
};

export function transformAliases(aliases: string[] | null): string[] {
  if (!aliases) {
    return [];
  }
  return aliases.filter((alias) => !!alias && !!alias.trim());
}

function transformHousingProducts(housingProduct: HousingDTO): HousingProduct {
  return {
    ...housingProduct,
    type: HousingProductType[housingProduct.type?.code ?? ""] ?? HousingProductType.NOT_HOUSING_PRODUCT_TYPE,
    product: transformProduct(housingProduct.product),
  };
}
